import * as React from "react";
import styled from "styled-components";

import { IS_BROWSER } from "@config/consts";
import { CLIENT_SWITCH_USER_LINK, AGENT_SWITCH_USER_LINK } from "@config/links";

import { AVAILABLE_LANGS } from "@typings/globals";
import { USER_ROLES } from "@typings/user";

import { SystemError } from "@ui/Organisms";

import { showToast } from "@containers/Toastify";

import { useAuth, useAuthManager } from "@hooks/useAuth";
import { useLocalisation } from "@hooks/useLocalisation";

const getImpersonateRedirectUrl = (
  email: string,
  lang: AVAILABLE_LANGS,
  isUserAgent?: boolean,
): string => {
  const langPrefix = `/${lang}`;
  if (isUserAgent) {
    return `${langPrefix}${AGENT_SWITCH_USER_LINK.replace("{email}", email)}`;
  }

  return `${langPrefix}${CLIENT_SWITCH_USER_LINK.replace(
    "{lang}",
    lang,
  ).replace("{email}", email)}`;
};

interface InitialImpersonating {
  feistelHash: number | null;
  firstName: string;
  lastName: string;
  email: string;
  isAgent: boolean;
}

// no need to translate, admin only
const ImpersonateChecker = (): React.ReactElement | null => {
  const { isUserAdmin, user } = useAuth();
  const { reloadUserData } = useAuthManager();
  const { language } = useLocalisation();

  const [isSessionCorrupted, setIsSessionCorrupted] =
    React.useState<boolean>(false);
  const [currentlyImpersonating, setCurrentlyImpersonating] =
    React.useState<string>("");
  const [initialImpersonating, setInitialImpersonating] = React.useState<
    InitialImpersonating | undefined
  >(undefined);

  const visibilityCheckEvent = async () => {
    if (document.visibilityState === "visible") {
      const { onError, onSuccess } = await reloadUserData();

      const errMessage =
        "The session cannot be checked automatically. Please ensure that you're logged in as a correct user.";

      onSuccess(payload => {
        if (initialImpersonating) {
          setIsSessionCorrupted(
            initialImpersonating.feistelHash !== payload.data.feistelHash,
          );
          setCurrentlyImpersonating(
            `${payload.data?.firstName} ${payload.data?.lastName}`,
          );
        } else {
          showToast(errMessage, { type: "dark" });
          setIsSessionCorrupted(false);
        }
      });

      onError(() => {
        const errMessage =
          "The session cannot be checked automatically. Please ensure that you're logged in as a correct user.";
        showToast(errMessage, { type: "dark" });
        setIsSessionCorrupted(false);
      });
    }
  };

  React.useEffect(() => {
    if (
      IS_BROWSER &&
      isUserAdmin &&
      initialImpersonating &&
      initialImpersonating.feistelHash
    ) {
      window.addEventListener("visibilitychange", visibilityCheckEvent);
    }

    return () => {
      window.removeEventListener("visibilitychange", visibilityCheckEvent);
    };
  }, [initialImpersonating, isUserAdmin]);

  React.useEffect(() => {
    if (user && !initialImpersonating) {
      const roles = user!.get("roles") as USER_ROLES[];

      setInitialImpersonating({
        feistelHash: user.get("feistelHash"),
        firstName: user.get("firstName"),
        lastName: user.get("lastName"),
        email: user.get("email"),
        isAgent: roles.includes(USER_ROLES.ROLE_AGENT),
      });
    }
  }, [user]);

  return !isUserAdmin || !isSessionCorrupted || !initialImpersonating ? null : (
    <CorruptedSessionWrapper>
      <Content>
        <SystemError
          titleText={`You are currently impersonating ${currentlyImpersonating}`}
          bodyText={
            <div>
              When you opened this tab, you were impersonating{" "}
              <b>
                {initialImpersonating?.firstName}{" "}
                {initialImpersonating?.lastName}
              </b>
              .<br />
              If you continue in this tab, it will crash.
            </div>
          }
          btnText={`Impersonate as ${initialImpersonating.firstName} ${initialImpersonating.lastName}`}
          btnHref={getImpersonateRedirectUrl(
            initialImpersonating.email,
            language,
            initialImpersonating.isAgent,
          )}
        />
      </Content>
      <Overlay />
    </CorruptedSessionWrapper>
  );
};

export { ImpersonateChecker };

const CorruptedSessionWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;

  display: flex;
  justify-content: center;
  align-content: center;
  flex-direction: column;
  text-align: center;

  z-index: ${props => props.theme.zIndex.corruptedSession};
`;

const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;

  background: ${props => props.theme.palette.white};
  opacity: 0.8;
`;

const Content = styled.div`
  z-index: 1;
`;
